import { BarChart3, CalendarDays, Flame, Gauge, Target, Trophy } from 'lucide-react'
import { EXERCISE_COPY } from '../domain/music'
import { createScaleContext, scaleDisplayName } from '../domain/pitchContext'
import type { ExerciseKind, ProgressState, RoundResult } from '../domain/types'

type ProgressProps = {
  progress: ProgressState
  onReset: () => void
}

const kinds: ExerciseKind[] = ['notes', 'chord-roots', 'chords', 'absolute-inversions', 'intervals', 'scales', 'inversions', 'progressions']

function contextLabel(round: RoundResult): string | null {
  if (round.questionSource === 'random') return 'random'
  if (round.questionSource === 'scale') {
    const context = createScaleContext(round.tonic ?? 0, round.scaleId ?? 'ionian')
    return `${context.tonicName} ${scaleDisplayName(context.scale)}`
  }
  return null
}

export function Progress({ progress, onReset }: ProgressProps) {
  const totalCorrect = progress.rounds.reduce((sum, round) => sum + round.correct, 0)
  const totalAnswers = progress.rounds.reduce((sum, round) => sum + round.total, 0)
  const accuracy = totalAnswers ? Math.round((totalCorrect / totalAnswers) * 100) : 0
  const averageResponse = progress.rounds.length
    ? progress.rounds.reduce((sum, round) => sum + round.averageResponseMs, 0) / progress.rounds.length / 1000
    : 0

  return (
    <main id="main-content" className="page-shell">
      <section className="page-hero compact">
        <div><h1>Progress</h1></div>
        <p>Completed rounds and results stored in this browser.</p>
      </section>

      <section className="metric-grid" aria-label="Progress summary">
        <div className="metric-card"><Flame aria-hidden="true" /><span>Current streak</span><strong>{progress.currentStreak} days</strong><small>Longest: {progress.longestStreak}</small></div>
        <div className="metric-card"><Target aria-hidden="true" /><span>Overall accuracy</span><strong>{totalAnswers ? `${accuracy}%` : '—'}</strong><small>{totalAnswers} answers</small></div>
        <div className="metric-card"><Trophy aria-hidden="true" /><span>Rounds complete</span><strong>{progress.rounds.length}</strong><small>Practice + scored</small></div>
        <div className="metric-card"><Gauge aria-hidden="true" /><span>Average response</span><strong>{progress.rounds.length ? `${averageResponse.toFixed(1)}s` : '—'}</strong><small>Across saved rounds</small></div>
      </section>

      <section className="progress-layout">
        <div className="panel skill-panel">
          <div className="panel-heading"><div><h2>Results by exercise</h2></div><BarChart3 aria-hidden="true" /></div>
          <div className="skill-bars">
            {kinds.map((kind) => {
              const rounds = progress.rounds.filter((round) => round.kind === kind)
              const correct = rounds.reduce((sum, round) => sum + round.correct, 0)
              const total = rounds.reduce((sum, round) => sum + round.total, 0)
              const score = total ? Math.round((correct / total) * 100) : 0
              return (
                <div className="skill-row" key={kind}>
                  <div><strong>{EXERCISE_COPY[kind].title}</strong><span>{rounds.length} rounds</span></div>
                  <div className="skill-track"><span style={{ width: `${score}%` }} /></div>
                  <b>{total ? `${score}%` : '—'}</b>
                </div>
              )
            })}
          </div>
        </div>

        <div className="panel history-panel">
          <div className="panel-heading"><div><h2>Recent rounds</h2></div><CalendarDays aria-hidden="true" /></div>
          {progress.rounds.length === 0 ? (
            <div className="empty-state"><span>♪</span><strong>No rounds yet</strong><p>Complete a practice or scored round and it will appear here.</p></div>
          ) : (
            <div className="history-list">
              {progress.rounds.slice(0, 8).map((round) => (
                <div className="history-row" key={round.id}>
                  <div><strong>{EXERCISE_COPY[round.kind].title}</strong><span>{new Date(round.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · {round.mode}{contextLabel(round) ? ` · ${contextLabel(round)}` : ''}</span></div>
                  <b>{Math.round(round.correct / round.total * 100)}%</b>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {progress.rounds.length > 0 && (
        <section className="danger-zone">
          <div><strong>Reset local progress</strong><p>This permanently removes saved rounds and preferences from this browser.</p></div>
          <button className="button button-danger" type="button" onClick={onReset}>Reset data</button>
        </section>
      )}
    </main>
  )
}
